import { useState } from 'react';
import classNames from 'classnames';
import styles from './OtherBets.module.css';
import { BetOdd } from 'components/BetOdd/BetOdd';
import { Bet } from 'types/Bet';
import { MatchOffer } from 'types/MatchOffer';
import { LiveEvent, LiveEventBet } from 'types/LiveEvent';
import { useTranslation } from 'react-i18next';
import { translateBetMarketName, translateOddText } from 'helpers/betI18n';
import {
  correctScore,
  overUnderLines,
  pickLabelText,
  splitLiveHandicap,
  type MarketCard,
} from 'helpers/marketLines';
import { describeMarket } from 'helpers/marketDescriptions';

interface Props {
  bets: Bet[] | LiveEventBet[];
  match: MatchOffer | LiveEvent;
  noBg?: boolean;
  noTopBorder?: boolean;
  isLive?: boolean;
}
/** Long pick names: a team or a player in the name, not "1", "Preko". */
const LONG_PICK = 12;

function pickColumns(bet: MarketCard): 2 | 3 | 4 {
  const odds = bet.odds ?? [];
  const label = (o: (typeof odds)[number]) =>
    // bwin and Tottalo picks have text ids ("bw...", "tc...").
    bet.pickLabels?.[o.custom_id as unknown as number] ??
    [o.name, o.handicap, o.header].filter(Boolean).join(' ');
  if (odds.some((o) => String(label(o)).length > LONG_PICK)) return 2;
  if (overUnderLines(odds)) return 2;
  return odds.length === 4 ? 4 : 3;
}

export const OtherBets = ({
  bets,
  match,
  noBg = false,
  noTopBorder = false,
  isLive = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  // Markets whose explanation the player opened with the "i" button.
  const [explained, setExplained] = useState<Set<string>>(new Set());
  const toggleExplained = (name: string) =>
    setExplained((open) => {
      const next = new Set(open);
      if (!next.delete(name)) next.add(name);
      return next;
    });

  // Live handicaps one card per line, as forzza.com lists them.
  const teams = { home: match.home?.name, away: match.away?.name };
  const shown: (Bet | LiveEventBet | MarketCard)[] = isLive
    ? (bets as (Bet | LiveEventBet)[]).flatMap(
        (b) =>
          (b &&
            !(b as MarketCard).pickLabels &&
            splitLiveHandicap(b as Bet, teams)) || [b]
      )
    : bets;

  return (
    <>
      {shown &&
        shown.length > 0 &&
        shown.map((bet, index) => {
          if (!bet || bet.odds?.length === 0) return <div key={index}></div>;
          // Live markets come unsplit: name the correct score's teams here.
          const x: MarketCard = (bet as MarketCard).pickLabels
            ? (bet as MarketCard)
            : correctScore(bet as Bet, {
                home: match.home?.name,
                away: match.away?.name,
              }) ?? (bet as MarketCard);
          const description = describeMarket(x.name, i18n.language);
          const key = `${x.name}-${index}`;
          return (
            <div
              key={index}
              className={classNames(styles.wrapper, {
                [styles['noBg']]: noBg,
                [styles['noTopBorder']]: noTopBorder,
              })}
            >
              <div className={styles.betName}>
                <span>
                  {translateBetMarketName(x, t)}
                  {x.part && ` – ${t(`lines_part_${x.part}`)}`}
                  {x.line && ` ${x.line}`}
                </span>
                {description && (
                  <button
                    type="button"
                    className={classNames(styles.info, {
                      [styles.infoOpen]: explained.has(key),
                    })}
                    aria-label={t('market_description_toggle')}
                    aria-expanded={explained.has(key)}
                    onClick={() => toggleExplained(key)}
                  >
                    i
                  </button>
                )}
                {description && explained.has(key) && (
                  <div className={styles.description}>{description}</div>
                )}
              </div>
              <div
                className={classNames(styles.oddsWrapper, {
                  // Picks named after players need room for the name.
                  [styles.playerOdds]: /scorer/i.test(x.name ?? ''),
                })}
                // Columns line up as 1 / X / 2 (four for GG & 3+ style
                // markets); fewer picks sit at the right, as on betbigwin.
                // Picks named after teams or players get two wide columns.
                data-cols={pickColumns(x as MarketCard)}
                data-count={x.odds?.length}
              >
                {x.odds?.map((y, index) => {
                  const short = (x as MarketCard).pickLabels?.[
                    y.custom_id as unknown as number
                  ];
                  return (
                    <div key={index}>
                      <BetOdd
                        odd={y}
                        bet={x}
                        match={match}
                        isLive={isLive}
                        label={
                          short
                            ? pickLabelText(short, (w) =>
                                translateOddText(w, t)
                              )
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </>
  );
};
